import React, { createContext, useContext, useEffect, useRef, useState } from 'react';

type ROSContextValue = {
  connected: boolean;
  connect: (wsUrl: string) => void;
  send: (msg: string) => void;
  onData: (cb: (data: string) => void) => () => void;
  getSession: () => string | null;
  clearSession: () => void;
  clearAll: () => void;
};

const ROSContext = createContext<ROSContextValue | null>(null);

export const SESSION_KEY = 'ros_ws_session';
export const QR_KEY = 'ros_ws_qr';
export const MOBILE_URL_KEY = 'ros_ws_mobile_url';

/**
 * Store Assistant pairing server. Migrated from the standalone Mac Mini backend
 * (ros-pair.1pn.in) onto the Samvaad server. Override via REACT_APP_PAIR_BASE_URL.
 */
export const PAIR_BASE_URL = process.env.REACT_APP_PAIR_BASE_URL || 'https://api-samvaad.the65.in';

const PAIR_HOST = (() => {
  try {
    return new URL(PAIR_BASE_URL).host;
  } catch {
    return 'api-samvaad.the65.in';
  }
})();

export const SAWSProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const wsRef = useRef<WebSocket | null>(null);
  const handlerRef = useRef<(d: string) => void>();
  const [connected, setConnected] = useState(false);

  const isConnectingRef = useRef(false);

  const getSession = () => localStorage.getItem(SESSION_KEY);

  const setSession = (wsUrl: string) => {
    localStorage.setItem(SESSION_KEY, wsUrl);
  };

  const clearSession = () => {
    localStorage.removeItem(SESSION_KEY);
  };

  // 🔥 NEW: full cleanup
  const clearAll = () => {
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(QR_KEY);
    localStorage.removeItem(MOBILE_URL_KEY);

    try {
      wsRef.current?.close();
    } catch (err) {
      console.log(err);
    }

    wsRef.current = null;
    setConnected(false);
  };

  const connect = (wsUrl: string) => {
    if (
      wsRef.current &&
      (wsRef.current.readyState === WebSocket.OPEN ||
        wsRef.current.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    isConnectingRef.current = true;
    setSession(wsUrl);

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      isConnectingRef.current = false;
      setConnected(true);
    };

    ws.onclose = () => {
      isConnectingRef.current = false;
      setConnected(false);
    };

    ws.onerror = () => {
      isConnectingRef.current = false;
      setConnected(false);
    };

    ws.onmessage = (e) => handlerRef.current?.(e.data);
  };

  useEffect(() => {
    if (wsRef.current) return;

    const saved = getSession();
    // One-time cutover: drop sessions from the old pairing server so we re-pair
    // cleanly on the new (Samvaad) infra instead of retrying a dead host.
    if (saved && !saved.includes(PAIR_HOST)) {
      clearAll();
      return;
    }
    if (saved) connect(saved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const send = (msg: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(msg);
    }
  };

  const onData = (cb: (data: string) => void) => {
    handlerRef.current = cb;
    return () => {
      handlerRef.current = undefined;
    };
  };

  return (
    <ROSContext.Provider
      value={{ connected, connect, send, onData, getSession, clearSession, clearAll }}
    >
      {children}
    </ROSContext.Provider>
  );
};

export const useSAWS = () => {
  const ctx = useContext(ROSContext);
  if (!ctx) throw new Error('useROS must be used within SAWSProvider');
  return ctx;
};
